import axios from 'axios';
import {
  ESPNLeague,
  ESPNTeam,
  ESPNMatchup,
  ApiResponse,
  League,
  Roster,
  Matchup,
  User,
  Player
} from '../types/fantasy';

const BASE_URL = 'https://lm-api-reads.fantasy.espn.com/apis/v3/games/ffl';

export class ESPNAPI {
  private async makeRequest<T>(endpoint: string, cookies?: string): Promise<ApiResponse<T>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (cookies) {
        headers['Cookie'] = cookies;
      }

      const fullUrl = `${BASE_URL}${endpoint}`;
      console.log(`ESPN: Making request to ${fullUrl}`);
      
      const response = await axios.get(fullUrl, { headers });
      
      console.log(`ESPN: Request successful, status: ${response.status}`);
      
      return {
        success: true,
        data: response.data,
        platform: 'espn'
      };
    } catch (error) {
      console.error(`ESPN: Request failed for ${BASE_URL}${endpoint}:`, error);
      
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // Add more specific error messages
      if (errorMessage.includes('404')) {
        errorMessage = 'League not found. Please check your League ID.';
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        errorMessage = 'Private league detected. Please provide ESPN cookies for authentication.';
      }
      
      return {
        success: false,
        error: errorMessage,
        platform: 'espn'
      };
    }
  }

  private getPositionName(positionId: number): string {
    const positions: Record<number, string> = {
      1: 'QB',
      2: 'RB',
      3: 'WR',
      4: 'TE',
      5: 'K',
      16: 'D/ST',
      17: 'FLEX',
      20: 'BENCH',
      21: 'IR',
      23: 'RB/WR/TE'
    };
    return positions[positionId] || 'UNKNOWN';
  }

  private getNFLTeamName(teamId: number): string {
    const teams: Record<number, string> = {
      1: 'ATL', 2: 'BUF', 3: 'CHI', 4: 'CIN', 5: 'CLE', 6: 'DAL', 7: 'DEN',
      8: 'DET', 9: 'GB', 10: 'TEN', 11: 'IND', 12: 'KC', 13: 'LV',
      14: 'LAR', 15: 'MIA', 16: 'MIN', 17: 'NE', 18: 'NO', 19: 'NYG',
      20: 'NYJ', 21: 'PHI', 22: 'ARI', 23: 'PIT', 24: 'LAC', 25: 'SF',
      26: 'SEA', 27: 'TB', 28: 'WSH', 29: 'CAR', 30: 'JAX', 33: 'BAL',
      34: 'HOU'
    };
    return teams[teamId] || 'UNK';
  }

  async getLeague(leagueId: string, season: number = new Date().getFullYear(), cookies?: string): Promise<ApiResponse<League>> {
    const views = ['mSettings', 'mStatus', 'mTeam'];
    const viewParams = views.map(view => `view=${view}`).join('&');
    const endpoint = `/seasons/${season}/segments/0/leagues/${leagueId}?${viewParams}`;
    
    const response = await this.makeRequest<ESPNLeague>(endpoint, cookies);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load league data',
        platform: 'espn'
      };
    }

    const espnLeague = response.data;
    const league: League = {
      id: espnLeague.id.toString(),
      name: espnLeague.settings.name,
      platform: 'espn',
      totalTeams: espnLeague.settings.size,
      currentWeek: espnLeague.status.currentMatchupPeriod,
      season: season,
      settings: {
        scoringType: espnLeague.settings.scoringSettings?.scoringType || 'unknown',
        playoffWeeks: espnLeague.settings.scheduleSettings.playoffTeamCount,
        regularSeasonWeeks: espnLeague.settings.scheduleSettings.regularSeasonMatchupPeriods
      }
    };

    return {
      success: true,
      data: league,
      platform: 'espn'
    };
  }

  async getLeagueUsers(leagueId: string, season: number = new Date().getFullYear(), cookies?: string): Promise<ApiResponse<User[]>> {
    const response = await this.makeRequest<ESPNLeague>(`/seasons/${season}/segments/0/leagues/${leagueId}?view=mTeam`, cookies);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load users data',
        platform: 'espn'
      };
    }

    const users: User[] = response.data.teams.map(team => {
      // Handle cases where location or nickname might be undefined or empty
      const teamName = this.getTeamName(team);
      return {
        userId: team.id.toString(),
        displayName: teamName,
        teamName: teamName
      };
    });

    return {
      success: true,
      data: users,
      platform: 'espn'
    };
  }
  
  private getTeamName(team: ESPNTeam): string {
    // Handle various cases of team naming
    const location = team.location?.trim() || '';
    const nickname = team.nickname?.trim() || '';
    const abbrev = team.abbrev?.trim() || '';
    
    // Debug logging to see what ESPN provides
    
    // If both exist, combine them
    if (location && nickname) {
      return `${location} ${nickname}`;
    }
    
    // If only one exists, use it (prefer nickname over location)
    if (nickname) return nickname;
    if (location) return location;
    
    // Try to create a more readable name from abbreviation
    if (abbrev) {
      // Convert abbreviation to a more readable format
      // For example: "TeamA" -> "Team A", "JS" -> "Team JS"
      const readableName = abbrev.length <= 3 ? `Team ${abbrev}` : abbrev;
      return readableName;
    }
    
    // Fallback to a generic name with team ID
    return `Team ${team.id}`;
  }

  async getLeagueRosters(leagueId: string, season: number = new Date().getFullYear(), week?: number, cookies?: string): Promise<ApiResponse<Roster[]>> {
    console.log(`ESPN: Fetching rosters for league ${leagueId}, season ${season}, week ${week}`);
    
    const views = ['mTeam', 'mRoster'];
    const viewParams = views.map(view => `view=${view}`).join('&');
    let endpoint = `/seasons/${season}/segments/0/leagues/${leagueId}?${viewParams}`;
    
    if (week) {
      endpoint += `&scoringPeriodId=${week}`;
    }
    
    console.log(`ESPN: API endpoint: ${BASE_URL}${endpoint}`);
    
    const response = await this.makeRequest<ESPNLeague>(endpoint, cookies);
    
    if (!response.success || !response.data) {
      console.error(`ESPN: Failed to fetch roster data:`, response.error);
      return {
        success: false,
        error: response.error || 'Failed to load roster data',
        platform: 'espn'
      };
    }

    console.log(`ESPN: Successfully fetched data for ${response.data.teams?.length || 0} teams`);

    const rosters: Roster[] = response.data.teams.map(team => {
      const players: Player[] = [];
      const starters: string[] = [];
      const bench: string[] = [];

      if (team.roster?.entries) {
        console.log(`ESPN: Processing ${team.roster.entries.length} roster entries for team ${this.getTeamName(team)}`);
        
        team.roster.entries.forEach((entry, index) => {
          // Check if playerPoolEntry exists
          if (!entry.playerPoolEntry?.player) {
            console.warn(`ESPN: Missing player data for entry ${index}:`, entry);
            return;
          }
          
          const espnPlayer = entry.playerPoolEntry.player;
          const player: Player = {
            id: entry.playerId.toString(),
            name: espnPlayer.fullName || `Player ${entry.playerId}`,
            position: this.getPositionName(espnPlayer.defaultPositionId),
            team: this.getNFLTeamName(espnPlayer.proTeamId)
          };
          
          players.push(player);
          
          // Determine if starter or bench based on lineup slot
          if (entry.lineupSlotId === 20) {
            bench.push(player.id);
          } else {
            starters.push(player.id);
          }
        });
      } else {
        console.warn(`ESPN: No roster entries found for team ${this.getTeamName(team)}`);
      }

      // Log roster data for debugging
      const teamName = this.getTeamName(team);
      console.log(`ESPN roster for ${teamName}: ${players.length} players`);
      if (players.length > 0) {
        console.log('Sample player:', players[0]);
      }

      return {
        userId: team.id.toString(),
        teamName: teamName,
        players,
        starters,
        bench,
        totalPoints: team.points || 0
      };
    });

    console.log(`ESPN: Successfully processed ${rosters.length} rosters with ${rosters.reduce((total, r) => total + r.players.length, 0)} total players`);

    return {
      success: true,
      data: rosters,
      platform: 'espn'
    };
  }

  async getMatchups(leagueId: string, season: number = new Date().getFullYear(), week: number, cookies?: string): Promise<ApiResponse<Matchup[]>> {
    // Include mTeam view to ensure team data is available
    const views = ['mMatchup', 'mMatchupScore', 'mTeam'];
    const viewParams = views.map(view => `view=${view}`).join('&');
    const endpoint = `/seasons/${season}/segments/0/leagues/${leagueId}?${viewParams}&scoringPeriodId=${week}`;
    
    const response = await this.makeRequest<ESPNLeague>(endpoint, cookies);
    
    if (!response.success || !response.data || !response.data.schedule) {
      return {
        success: false,
        error: 'No schedule data available',
        platform: 'espn'
      };
    }


    // Get team lookup for names
    const teamLookup = new Map<number, ESPNTeam>();
    if (response.data.teams) {
      response.data.teams.forEach(team => {
        teamLookup.set(team.id, team);
      });
    } else {
      // No teams data in ESPN matchup response
    }

    // If no teams data, try to get it separately
    if (!response.data.teams || response.data.teams.length === 0) {
      // No teams in matchup response, fetching separately
      const usersResponse = await this.getLeagueUsers(leagueId, season, cookies);
      if (usersResponse.success && usersResponse.data) {
        // Create synthetic team objects from users
        usersResponse.data.forEach(user => {
          const teamId = parseInt(user.userId);
          teamLookup.set(teamId, {
            id: teamId,
            location: '',
            nickname: user.displayName || `Team ${teamId}`,
            abbrev: '',
            owners: []
          } as ESPNTeam);
        });
      }
    }

    const matchups: Matchup[] = response.data.schedule
      .filter(match => match.matchupPeriodId === week)
      .map(match => {
        const homeTeam = teamLookup.get(match.home.teamId);
        const awayTeam = teamLookup.get(match.away.teamId);
        
        
        const getPlayersFromRoster = (roster: any) => {
          if (!roster?.entries) return [];
          return roster.entries.map((entry: any) => ({
            id: entry.playerId.toString(),
            name: entry.playerPoolEntry.player.fullName,
            position: this.getPositionName(entry.playerPoolEntry.player.defaultPositionId),
            team: this.getNFLTeamName(entry.playerPoolEntry.player.proTeamId)
          }));
        };

        const homeTeamName = homeTeam ? this.getTeamName(homeTeam) : `Team ${match.home.teamId}`;
        const awayTeamName = awayTeam ? this.getTeamName(awayTeam) : `Team ${match.away.teamId}`;
        

        return {
          week,
          team1: {
            userId: match.home.teamId.toString(),
            teamName: homeTeamName,
            points: match.home.totalPoints,
            roster: getPlayersFromRoster(match.home.rosterForCurrentScoringPeriod)
          },
          team2: {
            userId: match.away.teamId.toString(),
            teamName: awayTeamName,
            points: match.away.totalPoints,
            roster: getPlayersFromRoster(match.away.rosterForCurrentScoringPeriod)
          }
        };
      });

    return {
      success: true,
      data: matchups,
      platform: 'espn'
    };
  }

  // Utility method to get all league data at once
  async getLeagueData(leagueId: string, season: number = new Date().getFullYear(), week?: number, cookies?: string) {
    const currentWeek = week || 1;
    
    const [league, rosters, matchups] = await Promise.all([
      this.getLeague(leagueId, season, cookies),
      this.getLeagueRosters(leagueId, season, currentWeek, cookies),
      this.getMatchups(leagueId, season, currentWeek, cookies)
    ]);

    return {
      league,
      rosters,
      matchups
    };
  }

  // Helper method to check if league is private (requires cookies)
  async isLeaguePrivate(leagueId: string, season: number = new Date().getFullYear()): Promise<boolean> {
    try {
      const response = await this.getLeague(leagueId, season);
      return !response.success;
    } catch (error) {
      return true;
    }
  }
}

export const espnApi = new ESPNAPI();